import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiPost, apiDelete } from '../../../api/client';
import type { CustomVariable } from './types';

// ── Custom Variables Manager ──────────────────────────────────────────────────

export default function CustomVarsManager({ customVars, templateId }: { customVars: CustomVariable[]; templateId: string }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [newVar, setNewVar] = useState({ key: '', label: '', defaultValue: '' });

  const addMutation = useMutation({
    mutationFn: (v: { key: string; label: string; defaultValue: string }) =>
      apiPost('/contract-template/custom-variables', v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract-template'] });
      qc.invalidateQueries({ queryKey: ['contract-template-variables', templateId] });
      toast.success('Variabile aggiunta');
      setNewVar({ key: '', label: '', defaultValue: '' });
      setShowForm(false);
    },
    onError: (e: Error) => toast.error(e.message || 'Errore'),
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => apiDelete(`/contract-template/custom-variables/${key}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract-template'] });
      qc.invalidateQueries({ queryKey: ['contract-template-variables', templateId] });
      toast.success('Variabile rimossa');
    },
    onError: () => toast.error('Errore rimozione'),
  });

  return (
    <div style={{
      border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 'var(--radius-card)',
      background: 'var(--glass-bg-card)',
      padding: '0.625rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
        <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Variabili personalizzate</span>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            style={{
              background: 'none', border: '1px dashed rgba(255,255,255,0.10)',
              cursor: 'pointer', padding: '0.1rem 0.45rem',
              borderRadius: 'var(--radius-interactive)', fontSize: 'var(--text-xs)',
              color: 'var(--color-text-muted)',
            }}
          >
            + aggiungi
          </button>
        )}
      </div>

      {customVars.length === 0 && !showForm && (
        <p style={{ fontSize: 11, color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
          Nessuna variabile personalizzata. Aggiungine una per usare campi custom nel contratto.
        </p>
      )}

      {customVars.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem', marginBottom: showForm ? '0.375rem' : 0, overflowX: 'hidden' }}>
          {customVars.map(cv => (
            <span key={cv.key} style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
              fontSize: 10, fontFamily: 'monospace', wordBreak: 'break-all',
              padding: '0.1rem 0.35rem', borderRadius: 'var(--radius-interactive)',
              background: 'var(--color-warning-highlight)', border: '1px solid var(--color-warning)',
              color: 'var(--color-warning)',
              maxWidth: '100%', overflow: 'hidden',
            }} title={`${cv.label} · default: ${cv.defaultValue || '—'}`}>
              {cv.key}
              <span style={{ color: 'var(--color-text-faint)', fontSize: 9 }}>{cv.label}</span>
              <button
                onClick={() => { if (confirm(`Rimuovere {{${cv.key}}}?`)) deleteMutation.mutate(cv.key); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: 'var(--color-danger)' }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {showForm && (
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.375rem' }}>
          <input
            value={newVar.key}
            onChange={e => setNewVar(v => ({ ...v, key: e.target.value }))}
            placeholder="chiave snake_case"
            style={{
              flex: '1 1 110px', minWidth: 90, padding: '0.3rem 0.45rem',
              border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)',
              fontSize: 'var(--text-xs)', fontFamily: 'monospace',
              background: 'var(--glass-bg-card)', color: 'var(--color-text)',
            }}
          />
          <input
            value={newVar.label}
            onChange={e => setNewVar(v => ({ ...v, label: e.target.value }))}
            placeholder="etichetta"
            style={{
              flex: '1 1 90px', minWidth: 70, padding: '0.3rem 0.45rem',
              border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)',
              fontSize: 'var(--text-xs)', background: 'var(--glass-bg-card)', color: 'var(--color-text)',
            }}
          />
          <input
            value={newVar.defaultValue}
            onChange={e => setNewVar(v => ({ ...v, defaultValue: e.target.value }))}
            placeholder="valore default"
            style={{
              flex: '0 1 90px', minWidth: 60, padding: '0.3rem 0.45rem',
              border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)',
              fontSize: 'var(--text-xs)', background: 'var(--glass-bg-card)', color: 'var(--color-text)',
            }}
          />
          <div style={{ display: 'flex', gap: '0.2rem', alignSelf: 'flex-start' }}>
            <button
              onClick={() => { if (newVar.key && newVar.label) addMutation.mutate(newVar); }}
              disabled={!newVar.key || !newVar.label || addMutation.isPending}
              style={{
                padding: '0.3rem 0.55rem', border: 'none', borderRadius: 'var(--radius-interactive)',
                background: 'var(--color-primary)', color: 'white',
                cursor: 'pointer', fontSize: 'var(--text-xs)',
              }}
            >
              Salva
            </button>
            <button
              onClick={() => setShowForm(false)}
              style={{
                padding: '0.3rem 0.45rem', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)',
                background: 'none', color: 'var(--color-text-muted)',
                cursor: 'pointer', fontSize: 'var(--text-xs)',
              }}
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
